import { useCallback, useState, type FocusEvent, type PointerEvent } from "react";

export interface SidebarPeek {
  /** `true` mientras la barra plegada se está enseñando. */
  peeking: boolean;
  /** Para esparcir sobre el elemento que crece. */
  handlers: {
    onPointerEnter: (event: PointerEvent<HTMLElement>) => void;
    onPointerLeave: () => void;
    onFocus: (event: FocusEvent<HTMLElement>) => void;
    onBlur: (event: FocusEvent<HTMLElement>) => void;
  };
}

/**
 * Enseñar la barra lateral plegada sin desplegarla.
 *
 * ── Es una MIRADA, no un cambio de preferencia ──────────────────────
 * Plegarla es una decisión que se guarda; esto es un vistazo mientras el
 * cursor está encima. Por eso vive en estado local y no toca
 * `useSidebarCollapsed`: si escribiera ahí, pasar el ratón por el borde
 * de la pantalla desharía en silencio lo que la persona eligió.
 *
 * ── Por qué `pointerenter` y no `mouseenter` ────────────────────────
 * En una pantalla táctil, `mouseenter` se dispara al TOCAR. La barra se
 * abriría al intentar pulsar el botón de plegarla —el que está justo
 * ahí— y el gesto acabaría peleando consigo mismo. `pointerType` deja
 * pasar solo el ratón y el lápiz; el dedo no abre nada.
 *
 * ── Y por qué también el foco ───────────────────────────────────────
 * Quien navega con el tabulador nunca dispara un `pointerenter`. Sin
 * esto, la barra plegada le daría una fila de iconos sin nombre. El
 * `onBlur` se fija en `relatedTarget` para no cerrarla mientras el foco
 * salta de un enlace al siguiente DENTRO de la propia barra.
 */
export function useSidebarPeek(enabled: boolean): SidebarPeek {
  const [peeking, setPeeking] = useState(false);

  const open = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (enabled && event.pointerType !== "touch") setPeeking(true);
    },
    [enabled],
  );

  const close = useCallback(() => setPeeking(false), []);

  const onFocus = useCallback(() => {
    if (enabled) setPeeking(true);
  }, [enabled]);

  const onBlur = useCallback((event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    setPeeking(false);
  }, []);

  /*
   * Plegada de nuevo mientras se miraba: hay que soltar la mirada o la
   * barra se quedaría ancha con el ratón fuera. Se calcula al vuelo en
   * vez de en un efecto para no pintar un fotograma con el valor viejo.
   */
  return {
    peeking: enabled && peeking,
    handlers: { onPointerEnter: open, onPointerLeave: close, onFocus, onBlur },
  };
}
